import PropTypes from 'prop-types';
import { forwardRef, useState, useRef, useImperativeHandle } from 'react';

const NonEmpty = (isRequired, value) => {
    return !isRequired || value;
};

export const TextField = forwardRef(({ name, label, helperText, isRequired = false, validationFun = NonEmpty }, ref) => {
    const [valid, setValid] = useState(true);
    const [value, setValue] = useState('');
    const innerRef = useRef();

    const validate = () => {
        const isValid = validationFun(isRequired, value);
        setValid(isValid);
        return isValid;
    };

    useImperativeHandle(ref, () => ({
        validate,
        scrollIntoView: () => innerRef.current.scrollIntoView(),
    }));

    return (
        <div className="input-holder" ref={innerRef}>
            <label htmlFor={name} className={valid ? '' : 'hasError'}>
                {label} {isRequired && <span className="asterisk">&nbsp;*</span>}
            </label>
            <input type="text" id={name} name={name} value={value} onChange={e => setValue(e.target.value)} />
            {helperText &&
                <div className="helper-text">
                    {helperText}
                </div>
            }
            {!valid &&
                <div className="error-text">
                    {label} is required
                </div>
            }
        </div>
    );
});

TextField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    validationFun: PropTypes.func,
    helperText: PropTypes.node,
};

export const FileField = forwardRef(({ name, label, helperText, accepts, isRequired = false, validationFun = NonEmpty }, ref) => {
    const [valid, setValid] = useState(true);
    const [selectedFile, setSelectedFile] = useState();
    const innerRef = useRef();

    const fileChangeHandler = (event) => {
        setSelectedFile(event.target.files[0]);
    };

    const validate = () => {
        const isValid = !!selectedFile;
        setValid(isValid);
        return isValid;
    };

    useImperativeHandle(ref, () => ({
        validate,
        scrollIntoView: () => innerRef.current.scrollIntoView(),
    }));

    return (
        <div className="input-holder" ref={innerRef}>
            <label htmlFor={name} className={valid ? '' : 'hasError'}>
                {label} {isRequired && <span className="asterisk">&nbsp;*</span>}
            </label>
            <input type="file" id={name} name={name} onChange={fileChangeHandler} accept={accepts} />
            {helperText &&
                <div className="helper-text">
                    {helperText}
                </div>
            }
            {!valid &&
                <div className="error-text">
                    {label} is required
                </div>
            }
        </div>
    );
});

FileField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    validationFun: PropTypes.func,
    helperText: PropTypes.node,
    accepts: PropTypes.string,
};

const CheckBoxField = forwardRef(({ name, label, value, initialValue = false }, ref) => {
    const [checked, setChecked] = useState(initialValue);

    useImperativeHandle(ref, () => ({
        checked: () => checked,
    }));
    return (
        <li><span>
            <input name={name} id={value} type="checkbox" value={value} checked={checked} onChange={e => setChecked(!checked)} />
            <label htmlFor={value}>{label}</label>
        </span></li>
    );
});

CheckBoxField.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    initialValue: PropTypes.bool,
};

const NonEmptyCheckbox = (isRequired, refs) => {
    return !isRequired || refs.current.some(ref => ref.checked());
};

export const CheckBoxGroup = forwardRef(({ name, boxes, isRequired, label, validationFun = NonEmptyCheckbox }, ref) => {
    const boxRefs = useRef([]);
    const [valid, setValid] = useState(true);
    const innerRef = useRef();

    const validate = () => {
        const isValid = validationFun(isRequired, boxRefs);
        setValid(isValid);
        return isValid;
    };

    useImperativeHandle(ref, () => ({
        validate,
        scrollIntoView: () => innerRef.current.scrollIntoView(),
    }));

    return (
        <div className='checkbox-button-block' ref={innerRef}>
            {label} {isRequired && <span className="asterisk">&nbsp;*</span> }
            <ul className={valid ? 'add-tag' : 'add-tag hasError'}>
                {
                    boxes.map((box, i) => (
                        <CheckBoxField
                            ref={(el) => (boxRefs.current[i] = el)} name={name} value={box.value}
                            label={box.label} key={box.value} initialValue={box.initialValue} />
                    ))
                }
            </ul>
            {!valid &&
                <div className="error-text">
                    Please make a selection
                </div>
            }
        </div>
    );
});

CheckBoxGroup.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    validationFun: PropTypes.func,
    boxes: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
            initialValue: PropTypes.bool,
        }),
    ),
};

export const SelectGroup = forwardRef(({ name, options, isRequired, label, initialValue = '', validationFun = NonEmpty }, ref) => {
    const optionRefs = useRef([]);
    const [valid, setValid] = useState(true);
    const [value, setValue] = useState(initialValue);
    const innerRef = useRef();

    const validate = () => {
        const isValid = validationFun(isRequired, value);
        setValid(isValid);
        return isValid;
    };

    useImperativeHandle(ref, () => ({
        validate,
        scrollIntoView: () => innerRef.current.scrollIntoView(),
    }));

    return (
        <div className="input-holder select-holder" ref={innerRef}>
            <label htmlFor={name}>{label} { isRequired && <span className="asterisk">&nbsp;*</span> }</label>
            <select id={name} className={valid ? '' : ' hasError'} name={name} onChange={e => setValue(e.target.value)}>
                {
                    options.map((option, i) => (
                        <option
                            ref={(el) => (optionRefs.current[i] = el)} value={option.value}
                            key={option.value} selected={value === option.value}>
                            {option.label}
                        </option>
                    ))
                }
            </select>
            {!valid &&
                <div className="error-text">
                    Please make a selection
                </div>
            }
        </div>
    );
});

SelectGroup.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    validationFun: PropTypes.func,
    initialValue: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired
        }),
    ),
};
